// screens/ComplaintDetailScreen.js - The complaint dossier ("order file report").
// Header with the enrichment stored at creation and the editable references, the order's
// PhotoQA production photos (the evidence the factory keeps asking for), attachments such
// as the Oranje report, the complaint lines with the guided editor, and the event
// timeline. Site-scoped users get the same page read-only, without cost fields.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BottomNav } from './HomeScreen';
import { RefsForm } from './ComplaintNewScreen';
import { SyncBar, PhotoThumb, SectionHeader, useSmartBack } from '../components/shared';
import PhotoViewer from '../components/PhotoViewer';
import ComplaintLineEditor from '../components/ComplaintLineEditor';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import useComplaintTaxonomy from '../hooks/useComplaintTaxonomy';
import api, { getErrorMessage } from '../utils/api';
import {
  STATUS_BADGE, canEditComplaints, formatDay, formatMoment, leadTimeDays, numberEvents,
  orderSummary, toViewerPhoto, valueLabel,
} from '../utils/complaints';

const REF_FIELDS = ['retailer_ref', 'consumer_name', 'service_partner', 'service_case_ref', 'notes'];

export default function ComplaintDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = useSmartBack('/complaints/list');
  const { user } = useAuth();
  const { t } = useLang();
  const toast = useToast();
  const { taxonomy, error: taxError } = useComplaintTaxonomy();
  const canEdit = canEditComplaints(user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [addingLine, setAddingLine] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editRefs, setEditRefs] = useState(null);
  const [savingRefs, setSavingRefs] = useState(false);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const autoOpened = useRef(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data: d } = await api.get(`/api/complaints/${id}`);
      setData(d);
      setLoadError(null);
      return d;
    } catch (err) {
      setLoadError(getErrorMessage(err));
      return null;
    } finally { if (!quiet) setLoading(false); }
  }, [id]);

  useEffect(() => {
    load().then((d) => {
      if (!d || autoOpened.current) return;
      autoOpened.current = true;
      const lines = d.lines || [];
      if (!canEdit) return;
      // Straight from "Register" (or sent here because the order already had a dossier):
      // open a new line so registration continues where the user meant to be.
      if (location.state?.addLine) { setAddingLine(true); return; }
      // One line still in progress: open it, that is what the user came for.
      const inProgress = lines.filter((l) => l.status !== 'solved' && l.status !== 'cancelled');
      if (inProgress.length === 1) setExpanded(inProgress[0].id);
    });
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyFactoryLink = async () => {
    const url = `${window.location.origin}/complaints/${id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      ta.remove();
    }
    toast.success(t('complaints.link.copied'));
  };

  const saveRefs = async () => {
    setSavingRefs(true);
    try {
      const body = {};
      REF_FIELDS.forEach((k) => { body[k] = editRefs[k] && String(editRefs[k]).trim() ? String(editRefs[k]).trim() : null; });
      await api.patch(`/api/complaints/${id}`, body);
      await load(true);
      setEditRefs(null);
      toast.success(t('complaints.refs.saved'));
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSavingRefs(false); }
  };

  const uploadFiles = async (files, lineId = null) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', file.type.startsWith('image/') ? 'photo' : file.type === 'application/pdf' ? 'report' : 'other');
        if (lineId) fd.append('line_id', lineId);
        await api.post(`/api/complaints/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      }
      toast.success(list.length === 1 ? t('complaints.uploaded') : `${list.length} ${t('complaints.uploaded.many')}`);
      await load(true);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setUploading(false); }
  };

  const deleteAttachment = async (attId) => {
    const prev = data;
    setData((d) => ({ ...d, attachments: (d.attachments || []).filter((a) => a.id !== attId) }));
    try {
      await api.delete(`/api/complaints/${id}/attachments/${attId}`);
      load(true);
    } catch (err) { setData(prev); toast.error(getErrorMessage(err)); }
  };

  const onLineSaved = async (line) => {
    setAddingLine(false);
    setExpanded(null);
    if (line?.id) {
      setData((d) => {
        const lines = d.lines || [];
        const exists = lines.some((l) => l.id === line.id);
        return { ...d, lines: exists ? lines.map((l) => (l.id === line.id ? line : l)) : [...lines, line] };
      });
    }
    load(true);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  if (!data) return (
    <div className="screen">
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={back} aria-label={t('complaints.back')}>←</button>
        <h2>{t('complaints.dossier')}</h2>
        <div style={{ width: 52 }} />
      </div>
      <div className="page-content"><div className="error-banner">{loadError || t('complaints.loadfailed')}</div></div>
      <BottomNav active="complaints" />
    </div>
  );

  const { complaint: c, lines = [], events = [], attachments = [], photos = [], documents = [], open_service_orders: sos = [] } = data;
  const sortedLines = [...lines].sort((a, b) => (a.line_no || 0) - (b.line_no || 0));
  const summary = orderSummary(lines);
  const viewerPhotos = photos.map(toViewerPhoto);
  const lineNo = (lineId) => lines.find((l) => l.id === lineId)?.line_no;

  return (
    <div className="screen">
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={back} aria-label={t('complaints.back')}>←</button>
        <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.9375rem', color: 'var(--accent-primary)' }}>
            {c.order_number}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[c.customer_name, c.model].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span className={`badge ${summary.total > 0 && summary.solved === summary.total ? 'badge-success' : summary.total === 0 ? 'badge-secondary' : 'badge-warning'}`}>
          {t('complaints.summary').replace('{solved}', summary.solved).replace('{total}', summary.total)}
        </span>
      </div>

      <div className="page-content">
        {taxError && <div className="error-banner" style={{ marginBottom: 12 }}>{t('complaints.taxonomy.failed')}</div>}
        <div className="desktop-two-col">
          <div style={{ minWidth: 0 }}>
            {/* Order + references */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="meta-grid">
                <Meta label={t('complaints.customer')} value={[c.customer_code, c.customer_name].filter(Boolean).join(' · ')} />
                <Meta label={t('complaints.model')} value={c.model} />
                <Meta label={t('complaints.fabric')} value={c.fabric} />
                <Meta label={t('complaints.factory')} value={c.factory} />
                <Meta label={c.production_date_source === 'delivery' ? t('complaints.deliverydate') : t('complaints.productiondate')}
                  value={c.production_date ? formatDay(c.production_date) : null}
                  flag={c.production_date_source === 'delivery' ? t('complaints.fallback') : null}
                  title={c.production_date_source === 'delivery' ? t('complaints.fallback.title') : undefined} />
                <Meta label={t('complaints.delivered')} value={c.delivery_date ? formatDay(c.delivery_date) : null} />
                {c.commission_no && <Meta label={t('complaints.commission')} value={c.commission_no} />}
                <Meta label={t('complaints.registered')} value={`${formatDay(c.created_at)}${c.created_by_name ? ` · ${c.created_by_name}` : ''}`} />
              </div>
              {c.order_id && (
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, paddingLeft: 0 }} onClick={() => navigate(`/order/${c.order_id}`)}>
                  {t('complaints.openorder')} →
                </button>
              )}

              <div className="divider" />
              {editRefs ? (
                <>
                  <RefsForm form={editRefs} setForm={setEditRefs} partners={taxonomy?.servicePartners || []} t={t} />
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button className="btn btn-secondary" onClick={() => setEditRefs(null)}>{t('common.cancel')}</button>
                    <button className="btn btn-primary" disabled={savingRefs} onClick={saveRefs}>
                      {savingRefs ? t('complaints.saving') : t('common.save')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <SectionHeader title={t('complaints.refs')} action={canEdit ? (
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditRefs(Object.fromEntries(REF_FIELDS.map((k) => [k, c[k] || ''])))}>
                      ✎ {t('complaints.edit')}
                    </button>
                  ) : null} />
                  <div className="meta-grid">
                    <Meta label={t('complaints.retailerref')} value={c.retailer_ref} />
                    <Meta label={t('complaints.consumer')} value={c.consumer_name} />
                    <Meta label={t('complaints.partner')} value={c.service_partner} />
                    <Meta label={t('complaints.caseref')} value={c.service_case_ref} />
                  </div>
                  {c.notes && <p style={{ marginTop: 10, fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{c.notes}</p>}
                </>
              )}
            </div>

            {/* Complaint lines */}
            <div className="card" style={{ marginBottom: 16 }}>
              <SectionHeader title={`${t('complaints.lines')} (${lines.length})`} action={canEdit && !addingLine && taxonomy ? (
                <button className="btn btn-secondary btn-sm" onClick={() => { setAddingLine(true); setExpanded(null); }}>+ {t('complaints.addline')}</button>
              ) : null} />
              {lines.length === 0 && !addingLine && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t('complaints.nolines')}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sortedLines.map((l) => (
                  <LineCard key={l.id} line={l} complaintId={id} taxonomy={taxonomy} canEdit={canEdit}
                    showCost={!user?.site_id}
                    expanded={expanded === l.id}
                    onExpand={() => { setExpanded(l.id); setAddingLine(false); }}
                    onCollapse={() => setExpanded(null)}
                    onSaved={onLineSaved}
                    onChanged={() => load(true)}
                    onAttach={(files) => uploadFiles(files, l.id)}
                    attachments={attachments.filter((a) => a.line_id === l.id)}
                    t={t} />
                ))}
                {addingLine && taxonomy && (
                  <div style={{ border: '1px solid var(--border-accent)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>{t('complaints.newline')}</div>
                    <ComplaintLineEditor complaintId={id} line={null} taxonomy={taxonomy}
                      onSaved={onLineSaved} onClose={() => setAddingLine(false)} />
                  </div>
                )}
              </div>
            </div>

            {/* PhotoQA production photos */}
            <div className="card" style={{ marginBottom: 16 }}>
              <SectionHeader title={`${t('complaints.photos')} (${photos.length})`} action={canEdit ? (
                <button className="btn btn-secondary btn-sm" onClick={copyFactoryLink} title={t('complaints.link.title')}>
                  🔗 {t('complaints.link')}
                </button>
              ) : null} />
              {photos.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t('complaints.nophotos')}</div>
              ) : (
                <div className="photo-grid">
                  {viewerPhotos.map((p) => <PhotoThumb key={p.id} photo={p} onClick={() => setViewerPhoto(p)} />)}
                </div>
              )}
            </div>

            {/* Attachments: own uploads (Oranje / POS report, consumer photos) */}
            <div className="card"
              style={dragOver ? { outline: '2px dashed var(--accent-primary)', outlineOffset: 4 } : undefined}
              onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
              onDragLeave={canEdit ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); } : undefined}
              onDrop={canEdit ? (e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer?.files); } : undefined}>
              <SectionHeader title={`${t('complaints.attachments')} (${attachments.length})`} action={canEdit ? (
                <button className="btn btn-secondary btn-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                  {uploading ? t('complaints.uploading') : `⤓ ${t('complaints.upload')}`}
                </button>
              ) : null} />
              <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" style={{ display: 'none' }}
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
              {attachments.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                  {canEdit ? t('complaints.noattachments.edit') : t('complaints.noattachments')}
                </div>
              ) : (
                <AttachmentList items={attachments} lineNo={lineNo} canDelete={canEdit} onDelete={deleteAttachment} t={t} />
              )}
              {documents.length > 0 && (
                <>
                  <div className="section-title" style={{ marginTop: 16 }}>{t('complaints.orderuploads')}</div>
                  <AttachmentList items={documents.map((d) => ({ ...d, created_at: d.uploaded_at || d.created_at }))}
                    lineNo={() => null} canDelete={false} t={t} />
                </>
              )}
            </div>
          </div>

          {/* Right: service orders + timeline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            {sos.length > 0 && (
              <div className="card">
                <SectionHeader title={t('complaints.serviceorders')} />
                {sos.map((s) => (
                  <div key={s.order_no} className="activity-item">
                    <div className="activity-dot" />
                    <div>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{s.order_no}</span>
                      <span style={{ color: 'var(--text-muted)' }}> · {[s.type, s.status].filter(Boolean).join(' · ')}</span>
                      {s.created && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDay(s.created)}</div>}
                    </div>
                  </div>
                ))}
                <div className="form-hint" style={{ marginTop: 6 }}>{t('complaints.serviceorders.hint')}</div>
              </div>
            )}
            <div className="card">
              <SectionHeader title={t('complaints.timeline')} />
              {events.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t('complaints.noevents')}</div>
              ) : numberEvents(events).map((e) => (
                <div key={e.id} className="activity-item">
                  <div className="activity-dot" style={e.type === 'closed' ? { background: 'var(--accent-success)' } : e.type === 'cancelled' ? { background: 'var(--text-muted)' } : e.type === 'reopened' ? { background: 'var(--accent-warning)' } : undefined} />
                  <div style={{ minWidth: 0 }}>
                    {e.line_id && lineNo(e.line_id) != null && (
                      <span style={{ color: 'var(--text-muted)' }}>{t('complaints.line')} {lineNo(e.line_id)} · </span>
                    )}
                    <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                      {t(`complaints.event.${e.type}`, e.type.replace(/_/g, ' '))}{e.n ? ` (${e.n})` : ''}
                    </span>
                    {e.data?.reason && <span> · {e.data.reason}</span>}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {e.actor ? `${e.actor} · ` : ''}{formatMoment(e.at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {viewerPhoto && (
        <PhotoViewer photo={viewerPhoto} photos={viewerPhotos} onClose={() => setViewerPhoto(null)} />
      )}

      <BottomNav active="complaints" />
    </div>
  );
}

function Meta({ label, value, flag, title }) {
  return (
    <div title={title}>
      <div className="meta-label">{label}</div>
      <div className="meta-value">
        {value || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>–</span>}
        {flag && <span className="badge badge-warning" style={{ fontSize: '0.625rem', marginLeft: 6 }}>{flag}</span>}
      </div>
    </div>
  );
}

function AttachmentList({ items, lineNo, canDelete, onDelete, t }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((a) => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ fontSize: '1.125rem' }} aria-hidden="true">{a.kind === 'photo' ? '🖼' : a.kind === 'report' || a.kind === 'pdf' ? '📄' : '📎'}</span>
          <a href={a.url} target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, minWidth: 0, color: 'var(--accent-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
            {a.filename}
          </a>
          {lineNo(a.line_id) != null && <span className="badge badge-secondary" style={{ fontSize: '0.625rem' }}>{t('complaints.line')} {lineNo(a.line_id)}</span>}
          <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', flexShrink: 0 }}>
            {[a.uploaded_by_name?.split(' ')[0], formatDay(a.created_at)].filter(Boolean).join(' · ')}
          </span>
          {canDelete && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)', flexShrink: 0 }}
              aria-label={t('complaints.remove')} onClick={() => onDelete(a.id)}>✕</button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── One complaint line ────────────────────────────────────────────────────────
function LineCard({ line, complaintId, taxonomy, canEdit, showCost, expanded, onExpand, onCollapse, onSaved, onChanged, onAttach, attachments, t }) {
  const toast = useToast();
  const [action, setAction] = useState(null); // 'cancel' while the reason is being typed
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const status = line.status;
  const final = status === 'solved' || status === 'cancelled';
  const lead = leadTimeDays(line);
  const v = (f) => valueLabel(taxonomy, f, line[f]);

  const what = [v('article_type'), v('element_type')].filter(Boolean).join(' · ');
  const complaint = [v('main_complaint'), v('detail')].filter(Boolean).join(': ');

  // Reopen works on a solved line (back to incomplete verdict) and on a cancelled one
  // (undoes the cancel); cancel needs a reason.
  const runAction = async (kind) => {
    if (kind === 'cancel' && !reason.trim()) return;
    const wasSolved = status === 'solved';
    setBusy(true);
    try {
      await api.post(`/api/complaints/${complaintId}/lines/${line.id}/${kind}`, kind === 'cancel' ? { reason: reason.trim() } : {});
      toast.success(kind === 'cancel' ? t('complaints.line.cancelled')
        : wasSolved ? t('complaints.line.reopened') : t('complaints.line.uncancelled'));
      setAction(null);
      setReason('');
      await onChanged();
      if (kind === 'reopen') onExpand(); // straight back into the answers that need changing
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{
      border: `1px solid ${expanded ? 'var(--border-accent)' : 'var(--border-subtle)'}`,
      borderRadius: 'var(--radius-md)', padding: 14,
      opacity: status === 'cancelled' ? 0.65 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>{t('complaints.line')} {line.line_no}</span>
        <span className={`badge ${STATUS_BADGE[status] || 'badge-secondary'}`}>{t(`complaints.status.${status}`, status)}</span>
        {lead != null && status === 'solved' && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {t('complaints.leadtime').replace('{days}', lead)}{line.close_count > 1 ? ` · ${t('complaints.closedtimes').replace('{n}', line.close_count)}` : ''}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {canEdit && !final && !expanded && taxonomy && (
          <button className="btn btn-secondary btn-sm" onClick={onExpand}>{t('complaints.continue')}</button>
        )}
        {canEdit && expanded && (
          <button className="btn btn-ghost btn-sm" onClick={onCollapse}>{t('complaints.close')}</button>
        )}
      </div>

      {expanded && canEdit && !final ? (
        <div style={{ marginTop: 12 }}>
          <ComplaintLineEditor complaintId={complaintId} line={line} taxonomy={taxonomy} onSaved={onSaved} />
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: '0.875rem' }}>
          <div style={{ color: 'var(--text-primary)' }}>
            {complaint || <span style={{ color: 'var(--text-muted)' }}>{t('complaints.nodetail')}</span>}
            {what && <span style={{ color: 'var(--text-muted)' }}> · {what}</span>}
          </div>
          {line.description && <div style={{ color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{line.description}</div>}
          {(line.solution || line.causer || (showCost && line.cost_bearer)) && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, color: 'var(--text-secondary)' }}>
              {line.solution && <span><span style={{ color: 'var(--text-muted)' }}>{t('complaints.field.solution')}:</span> {v('solution')}</span>}
              {line.causer && <span><span style={{ color: 'var(--text-muted)' }}>{t('complaints.field.causer')}:</span> {v('causer')}</span>}
              {showCost && line.cost_bearer && <span><span style={{ color: 'var(--text-muted)' }}>{t('complaints.field.cost_bearer')}:</span> {line.cost_bearer}</span>}
            </div>
          )}
          {status === 'cancelled' && line.cancelled_reason && (
            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>{t('complaints.cancelreason')}: {line.cancelled_reason}</div>
          )}
        </div>
      )}

      {attachments.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.8125rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {attachments.map((a) => (
            <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>📎 {a.filename}</a>
          ))}
        </div>
      )}

      {canEdit && !action && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {status !== 'cancelled' && (
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📎 {t('complaints.attach')}</button>
          )}
          {/* A solved line is closed: the edit only opens after Reopen (server answers 409 otherwise). */}
          {status === 'solved' && (
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => runAction('reopen')}>↺ {t('complaints.reopen')}</button>
          )}
          {status === 'cancelled' ? (
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => runAction('reopen')}>↺ {t('complaints.uncancel')}</button>
          ) : (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }} onClick={() => setAction('cancel')}>
              {t('complaints.cancelline')}
            </button>
          )}
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" style={{ display: 'none' }}
            onChange={(e) => { onAttach(e.target.files); e.target.value = ''; }} />
        </div>
      )}

      {action === 'cancel' && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
          <label className="form-label" htmlFor={`reason-${line.id}`}>{t('complaints.cancel.reason')}</label>
          <div className="form-hint" style={{ margin: '4px 0 8px' }}>{t('complaints.cancel.hint')}</div>
          <input id={`reason-${line.id}`} className="input-field" value={reason} autoFocus
            onChange={(e) => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setAction(null); setReason(''); }}>{t('common.cancel')}</button>
            <button className="btn btn-sm btn-danger" disabled={busy || !reason.trim()} onClick={() => runAction('cancel')}>
              {t('complaints.cancelline')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
